import { MAX_BULK_INDEX_ZIP_MB } from '../../constants';
import i18n from '../../i18n';
import {
  API_CONFIG,
  API_ENDPOINTS,
  getHeadersWithOrg,
  getMultipartHeaders,
  apiCall,
  authFetch,
  notifyAuthResponseStatus,
} from './client';
import {
  ErrorResponse,
  FileStatus,
  PresignResponse,
  MultipartInitiateResponse,
  CompletedPart,
  ZipIndexResponse,
  IndexingStatusResponse,
  IndexCancelResponse,
  IndexingHistoryResponse,
} from '../../types';

export type BulkIndexSiteOptions = {
  /** Fired as the request body is sent (bytes uploaded / total when known). */
  onUploadProgress?: (loaded: number, total: number) => void;
  /** Org's effective max ZIP size (MB). Falls back to MAX_BULK_INDEX_ZIP_MB. */
  maxZipMb?: number;
};

/** Human-readable fallback when a bulk-index submission hits a full queue (429). */
const QUEUE_FULL_MESSAGE =
  "This site's indexing queue is full. Wait for a running or queued job to finish, then try again.";

/**
 * Thrown when a bulk-index submission is rejected with HTTP 429 because the
 * site's admission queue is already at capacity. Distinct from a generic
 * failure so the UI can tell the user to wait and retry rather than surfacing
 * it as a hard error. (A busy-but-not-full site returns 202 "waiting" instead,
 * which is a success -- see submitBulkIndexJob.)
 */
export class IndexQueueFullError extends Error {
  constructor(message: string = QUEUE_FULL_MESSAGE) {
    super(message);
    this.name = 'IndexQueueFullError';
  }
}

/**
 * POST a bulk-index job (endpoints bulk-index, bulk-index/confirm,
 * bulk-index/multipart/{jobId}/complete) and parse the ZipIndexResponse.
 *
 * Unlike the generic apiCall, this maps the admission-queue contract:
 * - 201 (idle site) and 202 (admitted to the queue, status "waiting") are both
 *   successes -- response.ok is true for each, so the ZipIndexResponse is returned.
 * - 429 (queue at MAX_QUEUE_DEPTH) is surfaced as IndexQueueFullError so callers
 *   can present distinct "queue full, retry later" messaging.
 * Every other non-2xx defers to apiCall's error shape ({"detail": "..."}).
 */
async function submitBulkIndexJob(url: string, init: RequestInit): Promise<ZipIndexResponse> {
  const response = await authFetch(url, init);
  if (response.status === 429) {
    const body: ErrorResponse = await response.json().catch(() => ({ error: 'HTTP 429' }));
    const detail = typeof body.detail === 'string' ? body.detail : null;
    throw new IndexQueueFullError(detail || undefined);
  }
  if (!response.ok) {
    const errorData: ErrorResponse = await response
      .json()
      .catch(() => ({ error: `HTTP ${response.status}` }));
    const detail = typeof errorData.detail === 'string' ? errorData.detail : null;
    throw new Error(detail || errorData.error || `Failed to start indexing: ${response.status}`);
  }
  return response.json();
}

function applyXhrHeaders(xhr: XMLHttpRequest, headers: HeadersInit): void {
  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      xhr.setRequestHeader(key, value);
    });
    return;
  }
  if (Array.isArray(headers)) {
    for (const pair of headers) {
      const [k, v] = pair;
      if (v !== undefined && v !== null) {
        xhr.setRequestHeader(k, String(v));
      }
    }
    return;
  }
  for (const [k, v] of Object.entries(headers)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      for (const item of v) {
        xhr.setRequestHeader(k, String(item));
      }
    } else {
      xhr.setRequestHeader(k, String(v));
    }
  }
}

/**
 * POST multipart FormData with upload progress. Uses XMLHttpRequest because
 * `fetch` does not expose upload progress events.
 */
function xhrPostMultipart(
  url: string,
  formData: FormData,
  orgId: string,
  file: File,
  onUploadProgress?: (loaded: number, total: number) => void,
): Promise<{ status: number; bodyText: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    applyXhrHeaders(xhr, getMultipartHeaders(orgId));

    xhr.upload.onprogress = (e) => {
      if (!onUploadProgress) return;
      const total = e.lengthComputable && e.total > 0 ? e.total : file.size;
      if (total > 0) {
        onUploadProgress(e.loaded, total);
      }
    };

    xhr.onload = () => {
      notifyAuthResponseStatus(xhr.status);
      resolve({ status: xhr.status, bodyText: xhr.responseText ?? '' });
    };
    xhr.onerror = () => {
      reject(new Error('Network error while uploading file'));
    };
    xhr.onabort = () => {
      reject(new Error('Upload cancelled'));
    };

    xhr.send(formData);
  });
}

/**
 * Upload a ZIP file for bulk indexing of a site.
 * Returns the created job details including job_id for polling.
 */
export async function bulkIndexSite(
  siteId: string,
  file: File,
  orgId: string,
  basePath?: string,
  options?: BulkIndexSiteOptions,
): Promise<ZipIndexResponse> {
  let url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.BULK_INDEX(siteId)}`;
  const params = new URLSearchParams();
  if (basePath && basePath.trim()) {
    params.set('base_path', basePath.trim());
  }
  const qs = params.toString();
  if (qs) {
    url += `?${qs}`;
  }

  const formData = new FormData();
  formData.append('file', file);

  try {
    const { status, bodyText } = await xhrPostMultipart(
      url,
      formData,
      orgId,
      file,
      options?.onUploadProgress,
    );

    if (status === 413) {
      throw new Error(
        `The ZIP file is too large. Maximum upload size is ${(options?.maxZipMb ?? MAX_BULK_INDEX_ZIP_MB).toLocaleString()} MB.`,
      );
    }

    let parsed: unknown;
    try {
      parsed = bodyText ? JSON.parse(bodyText) : null;
    } catch {
      throw new Error(`Failed to start indexing: HTTP ${status}`);
    }

    // Admission queue full (mirrors submitBulkIndexJob for the fetch-based flows).
    if (status === 429) {
      const detail = (parsed as ErrorResponse | null)?.detail;
      throw new IndexQueueFullError(typeof detail === 'string' ? detail : undefined);
    }

    if (status < 200 || status >= 300) {
      const errorData = parsed as ErrorResponse;
      const msg =
        (errorData && (errorData.detail || errorData.error)) ||
        `Failed to start indexing: ${status}`;
      throw new Error(msg);
    }

    return parsed as ZipIndexResponse;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('errors.indexing.uploadFailed'));
  }
}

/**
 * Start bulk indexing from a completed document-to-DITA conversion job.
 * The backend loads the DITA ZIP directly from S3 -- no file upload needed.
 */
export async function bulkIndexFromConversion(
  siteId: string,
  orgId: string,
  conversionJobId: string,
  basePath?: string,
  rootMapPath?: string,
): Promise<ZipIndexResponse> {
  let url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.BULK_INDEX(siteId)}`;
  const params = new URLSearchParams();
  params.set('conversion_job_id', conversionJobId);
  if (basePath && basePath.trim()) {
    params.set('base_path', basePath.trim());
  }
  if (rootMapPath?.trim()) {
    params.set('root_map_path', rootMapPath.trim());
  }
  url += `?${params.toString()}`;

  return submitBulkIndexJob(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
  });
}

// =============================================================================
// Presigned Upload (Valet Key Pattern)
// =============================================================================

/**
 * Request a presigned URL for direct-to-S3 ZIP upload.
 * Returns a job_id, upload_token, and a same-origin proxy URL.
 */
export async function requestPresignedUrl(
  siteId: string,
  orgId: string,
  basePath?: string,
): Promise<PresignResponse> {
  let url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.BULK_INDEX_PRESIGN(siteId)}`;
  const params = new URLSearchParams();
  if (basePath && basePath.trim()) {
    params.set('base_path', basePath.trim());
  }
  const qs = params.toString();
  if (qs) {
    url += `?${qs}`;
  }

  return apiCall<PresignResponse>(url, {
    headers: getHeadersWithOrg(),
  });
}

/**
 * Upload a file directly to S3 via the same-origin /s3-upload/ proxy.
 * Uses XMLHttpRequest for upload progress tracking.
 *
 * IMPORTANT: No auth headers are sent — the presigned URL query
 * string contains the S3 authorization.
 */
export function uploadToPresignedUrl(
  presignedUrl: string,
  file: File,
  onUploadProgress?: (loaded: number, total: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', 'application/zip');

    xhr.upload.onprogress = (e) => {
      if (!onUploadProgress) return;
      const total = e.lengthComputable && e.total > 0 ? e.total : file.size;
      if (total > 0) {
        onUploadProgress(e.loaded, total);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload to storage failed: HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => {
      reject(new Error('Network error while uploading file'));
    };
    xhr.onabort = () => {
      reject(new Error('Upload cancelled'));
    };

    xhr.send(file);
  });
}

/**
 * Confirm a presigned upload and start the indexing job.
 * Call this after uploadToPresignedUrl succeeds.
 */
export async function confirmUpload(
  siteId: string,
  orgId: string,
  jobId: string,
  uploadToken: string,
  basePath?: string,
  rootMapPath?: string,
): Promise<ZipIndexResponse> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.BULK_INDEX_CONFIRM(siteId)}`;

  const payload: Record<string, string> = {
    job_id: jobId,
    upload_token: uploadToken,
  };
  if (basePath && basePath.trim()) {
    payload.base_path = basePath.trim();
  }
  if (rootMapPath?.trim()) {
    payload.root_map_path = rootMapPath.trim();
  }

  return submitBulkIndexJob(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify(payload),
  });
}

// =============================================================================
// Chunked Multipart Upload (large ZIPs)
// =============================================================================

/**
 * Initiate a chunked S3 multipart upload for a large ZIP. Returns a
 * presigned PUT URL per part, plus the upload_id/upload_token needed to
 * complete or abort it.
 */
export async function initiateMultipartUpload(
  siteId: string,
  orgId: string,
  contentLengthBytes: number,
): Promise<MultipartInitiateResponse> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.BULK_INDEX_MULTIPART_INITIATE(siteId)}`;

  return apiCall<MultipartInitiateResponse>(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify({ content_length_bytes: contentLengthBytes }),
  });
}

/**
 * Upload one part directly to S3 via the same-origin /s3-upload/ proxy, and
 * read back its ETag. Does not know about concurrency or aggregate progress
 * across parts -- callers coordinate multiple parts themselves.
 *
 * IMPORTANT: No auth headers are sent -- the presigned URL query string
 * contains the S3 authorization (same as uploadToPresignedUrl).
 */
export function uploadPart(
  presignedUrl: string,
  partNumber: number,
  blob: Blob,
  onUploadProgress?: (loaded: number, total: number) => void,
): Promise<CompletedPart> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presignedUrl);

    xhr.upload.onprogress = (e) => {
      if (!onUploadProgress) return;
      const total = e.lengthComputable && e.total > 0 ? e.total : blob.size;
      if (total > 0) {
        onUploadProgress(e.loaded, total);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const rawEtag = xhr.getResponseHeader('ETag') ?? '';
        const etag = rawEtag.replace(/^"|"$/g, '');
        if (!etag) {
          reject(new Error(`Part ${partNumber} upload succeeded but response is missing an ETag`));
          return;
        }
        resolve({ part_number: partNumber, etag });
      } else {
        reject(new Error(`Part ${partNumber} upload failed: HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => {
      reject(new Error(`Network error while uploading part ${partNumber}`));
    };
    xhr.onabort = () => {
      reject(new Error(`Part ${partNumber} upload cancelled`));
    };

    xhr.send(blob);
  });
}

/**
 * Complete a multipart upload and start the indexing job. Call this after
 * every part has been uploaded via uploadPart.
 */
export async function completeMultipartUpload(
  siteId: string,
  orgId: string,
  jobId: string,
  uploadToken: string,
  uploadId: string,
  parts: CompletedPart[],
  basePath?: string,
  rootMapPath?: string,
): Promise<ZipIndexResponse> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.BULK_INDEX_MULTIPART_COMPLETE(siteId, jobId)}`;

  const payload: Record<string, unknown> = {
    upload_token: uploadToken,
    upload_id: uploadId,
    parts,
  };
  if (basePath && basePath.trim()) {
    payload.base_path = basePath.trim();
  }
  if (rootMapPath?.trim()) {
    payload.root_map_path = rootMapPath.trim();
  }

  return submitBulkIndexJob(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify(payload),
  });
}

/**
 * Abort a multipart upload (e.g. on user cancel or unrecoverable part
 * failure). Best-effort -- callers should not block error handling on this.
 */
export async function abortMultipartUpload(
  siteId: string,
  orgId: string,
  jobId: string,
  uploadToken: string,
  uploadId: string,
): Promise<{ status: string }> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.BULK_INDEX_MULTIPART_ABORT(siteId, jobId)}`;

  return apiCall<{ status: string }>(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify({ upload_token: uploadToken, upload_id: uploadId }),
  });
}

/**
 * Poll the indexing status for a site.
 *
 * Pass `offset` / `limit` to paginate the per-file `files` map.
 * Use `limit=0` for lightweight polling (summary counts only, no files).
 * Pass `jobId` to resolve that exact job instead of the site's
 * current/latest job -- needed once concurrent single-file saves can be
 * in flight on the same site simultaneously, since "latest job for site"
 * no longer reliably means "my job" in that case.
 */
export async function getIndexingStatus(
  siteId: string,
  orgId: string,
  opts?: { offset?: number; limit?: number; jobId?: string; status?: FileStatus['status'] },
): Promise<IndexingStatusResponse> {
  let url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.INDEX_STATUS(siteId)}`;
  const params = new URLSearchParams();
  if (opts?.offset !== undefined) {
    params.set('offset', String(opts.offset));
  }
  if (opts?.limit !== undefined) {
    params.set('limit', String(opts.limit));
  }
  if (opts?.jobId !== undefined) {
    params.set('job_id', opts.jobId);
  }
  if (opts?.status !== undefined) {
    params.set('status', opts.status);
  }
  const qs = params.toString();
  if (qs) {
    url += `?${qs}`;
  }

  return apiCall<IndexingStatusResponse>(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });
}

/**
 * Cancel an indexing job for a site.
 *
 * Pass `jobId` to target an exact job: when it names a still-queued (not yet
 * dispatched) admission, that queue entry is cancelled and its ZIP cleaned up.
 * Omitting it (or passing a job_id that is not a queued admission) keeps the
 * default behavior -- cancel the site's currently executing job.
 */
export async function cancelIndexingJob(
  siteId: string,
  orgId: string,
  jobId?: string,
): Promise<IndexCancelResponse> {
  let url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.INDEX_CANCEL(siteId)}`;
  if (jobId) {
    url += `?job_id=${encodeURIComponent(jobId)}`;
  }
  return apiCall<IndexCancelResponse>(url, {
    method: 'PUT',
    headers: getHeadersWithOrg(),
  });
}

export async function getIndexingHistory(
  siteId: string,
  orgId: string,
): Promise<IndexingHistoryResponse> {
  return apiCall<IndexingHistoryResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.BULK_INDEX_HISTORY(siteId)}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}
